import React from 'react';
import { SortBar, SortOption, SortColumn, DatePicker, CurrencyInputField } from './index';

// Define search field types
export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'currency' | 'dateRange' | 'currencyRange';
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
}

// Define the main props interface
export interface SearchListBarProps {
  // Basic configuration
  title: string;
  searchFields: SearchField[];
  sortColumns: SortColumn[];
  
  // Data
  searchParams: Record<string, any>;
  currentSorts: SortOption[];
  
  // Callbacks
  onSearchChange: (field: string, value: any) => void;
  onSortsChange: (sorts: SortOption[]) => void;
  
  // Optional configuration
  defaultSort?: SortOption;
  showSortBar?: boolean;
  className?: string;
}

const SearchListBarLegacy: React.FC<SearchListBarProps> = ({
  title,
  searchFields,
  sortColumns,
  searchParams,
  currentSorts,
  onSearchChange,
  onSortsChange,
  defaultSort,
  showSortBar = true,
  className = ''
}) => {
  const renderSearchField = (field: SearchField) => {
    const value = searchParams[field.key] || '';
    
    switch (field.type) {
      case 'text':
        return (
          <div className="app-form-group" key={field.key}>
            <label htmlFor={`search-${field.key}`} className="app-form-label">
              {field.label}
            </label>
            <input
              id={`search-${field.key}`}
              type="text"
              className="app-form-input"
              placeholder={field.placeholder || `Search ${field.label.toLowerCase()}...`}
              value={value}
              onChange={(e) => onSearchChange(field.key, e.target.value)}
            />
          </div>
        );
        
      case 'select':
        return (
          <div className="app-form-group" key={field.key}>
            <label htmlFor={`search-${field.key}`} className="app-form-label">
              {field.label}
            </label>
            <select
              id={`search-${field.key}`}
              className="app-form-select"
              value={value}
              onChange={(e) => onSearchChange(field.key, e.target.value)}
            >
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
        
      case 'date':
        return (
          <div className="app-form-group" key={field.key}>
            <label className="app-form-label">{field.label}</label>
            <DatePicker
              value={value}
              onChange={(date) => onSearchChange(field.key, date)}
              placeholder={field.placeholder || "mm/dd/yyyy"}
            />
          </div>
        );
        
      case 'currency':
        return (
          <div className="app-form-group" key={field.key}>
            <label className="app-form-label">{field.label}</label>
            <CurrencyInputField
              value={value || 0}
              onChange={(amount) => onSearchChange(field.key, amount > 0 ? amount : undefined)}
              placeholder={field.placeholder || "0.00"}
              min={field.min}
              max={field.max}
            />
          </div>
        );
        
      case 'dateRange':
        return (
          <React.Fragment key={field.key}>
            <div className="app-form-group">
              <label className="app-form-label">{`Start ${field.label}`}</label>
              <DatePicker
                value={searchParams[`${field.key}Start`] || ''}
                onChange={(date) => onSearchChange(`${field.key}Start`, date)}
                placeholder="mm/dd/yyyy"
              />
            </div>
            <div className="app-form-group">
              <label className="app-form-label">{`End ${field.label}`}</label>
              <DatePicker
                value={searchParams[`${field.key}End`] || ''}
                onChange={(date) => onSearchChange(`${field.key}End`, date)}
                placeholder="mm/dd/yyyy"
              />
            </div>
          </React.Fragment>
        );
        
      case 'currencyRange':
        return (
          <React.Fragment key={field.key}>
            <div className="app-form-group">
              <label className="app-form-label">{`Min ${field.label}`}</label>
              <CurrencyInputField
                value={searchParams[`${field.key}Min`] || 0}
                onChange={(amount) => onSearchChange(`${field.key}Min`, amount > 0 ? amount : undefined)}
                placeholder="0.00"
                min={field.min}
                max={field.max}
              />
            </div>
            <div className="app-form-group">
              <label className="app-form-label">{`Max ${field.label}`}</label>
              <CurrencyInputField
                value={searchParams[`${field.key}Max`] || 0}
                onChange={(amount) => onSearchChange(`${field.key}Max`, amount > 0 ? amount : undefined)}
                placeholder="0.00"
                min={field.min}
                max={field.max}
              />
            </div>
          </React.Fragment>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className={`search-list-bar ${className}`}>
      <div className="app-search-section">
        {/* Search Fields */}
        {searchFields.map(renderSearchField)}
        
        {/* Sort Bar */}
        {showSortBar && (
          <div className="sort-section">
            <SortBar
              availableColumns={sortColumns}
              currentSorts={currentSorts}
              onSortsChange={onSortsChange}
              defaultSort={defaultSort}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchListBarLegacy;
